import { cn } from '@/lib';
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * @component EmptyState
 * @description Renders a centred placeholder for lists or sections without content, such as
 *              "no results", "access denied" or "not configured" states.
 *
 * @props
 *   title (string, required) - Short headline of the state.
 *   description (string, optional) - Supporting sentence rendered under the title.
 *   icon (ReactNode, optional) - Icon rendered above the title.
 *   className (string, optional) - Extra classes for the wrapper.
 *
 * @slots
 *   children - Optional actions rendered under the description.
 *
 * @changelog
 *   2026-09-07 - Initial creation for the device rules and assignment tabs.
 */
export function EmptyState({ title, description, icon, children, className }: EmptyStateProps) {
  return (
    <div className={cn('text-onsurface/60 flex flex-col items-center justify-center py-12 text-center', className)}>
      {icon && <div className="mb-4 [&>svg]:size-12">{icon}</div>}
      <p className="text-onsurface text-sm font-medium">{title}</p>
      {description && <p className="text-muted mt-1 max-w-md text-sm">{description}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}
